import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ResetFormDataAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  AllowedCountryType,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { FormSubmitter } from '../../widgets/form-widgets/form-submitter/form-submitter.component';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-international-currency-country',
  templateUrl: './international-currency-country.component.html',
})
export class InternationalCurrencyCountryComponent extends FormSubmitter implements OnInit {
  public selectCountryForm: FormGroup;
  @Input() widgetConfig: UiZoneWidgetConfig;
  @Input() currencyCodes: { title: string; value: string }[];
  @Input() countryList: AllowedCountryType[];
  @Input() customStyle: baseCssConfigStyle;
  @Input() inputFieldStyle: baseCssConfigStyle;
  @Input() currentInactiveStyle: baseCssConfigStyle;
  @Input() currentActiveStyle: baseCssConfigStyle;
  @Input() continueButtonStyle: baseCssConfigStyle = {};
  @Input() continueButtonContent = 'Continue';
  @Input() showCurrencyFlags = true;
  @Input() public isSubmitTypeAsynchronous = false;
  @Input() globalStylingClass = '';
  @Input() isAllowedGlobalStyling = false;
  @Input() isNewAddBeneficiaryIntlExist = false;
  @Input() isScrollable = true;

  @Output() public FormChangeEvent = new EventEmitter<{
    currency: string;
    countryIso2: string;
    isCountryCurrencyFormValid: boolean;
  }>();

  public formGroup: FormGroup;
  public currentCurrencyStyle: baseCssConfigStyle;
  public currentCountryStyle: baseCssConfigStyle;
  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private formSubmissionService: FormSubmissionService,
    private _router: Router,
    private cdr: ChangeDetectorRef,
    public override store: Store,
    public pageConfig: PageConfigurationProvider,
  ) {
    super(store);
  }
  ngOnInit() {
    this.buildForm();
    this.currentCurrencyStyle = this.currentInactiveStyle;
    this.currentCountryStyle = this.currentInactiveStyle;
    if (this.isSubmitTypeAsynchronous) this.emitLatestValues();
  }
  public buildForm() {
    this.subscribeToFormAction(FormActionTypeEnum.INTL_BENEFICIARY_ADD).subscribe((response) => {
      const currencyValue = response?.formData?.['currency'];
      const countryValue = response?.formData?.['countryIso2'];

      // Check if currencyKey is present and find corresponding currency title
      const selectedCurrency = currencyValue
        ? (this.currencyCodes.find((currency) => currency.value === currencyValue) ?? this.currencyCodes[0])
        : this.currencyCodes[0];

      // Check if countryKey is present and find corresponding country
      const selectedCountry = countryValue
        ? (this.countryList.find((country) => country.key === countryValue) ?? this.countryList[0])
        : this.countryList[0];

      this.formGroup = new FormGroup({});
      for (const field of this.widgetConfig.attributes['field_attributes']) {
        const initialValue = field.name === 'currency' ? selectedCurrency : selectedCountry;
        this.formGroup.addControl(field.name, new FormControl(initialValue, []));
      }
    });
    this.selectCountryForm = this.formGroup;
  }

  public updateStyles(event: boolean, type: string) {
    if (type === 'currency') {
      this.currentCurrencyStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
    } else if (type === 'country') {
      this.currentCountryStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
    }
  }

  public onSubmit() {
    this.formSubmissionService.resetCurrentPageFormGroups(); // clear the formGroups to start the add intl beneficiary journey
    this.store.dispatch(new ResetFormDataAction()); // clear the formAction data first
    if (this.selectCountryForm.valid) {
      const formObject = this.selectCountryForm.value;
      this.formSubmissionService.submitIndividualForm(
        {
          currency: formObject.currency.title,
          countryIso2: formObject.country.key,
        },
        '',
        FormActionTypeEnum.INTL_BENEFICIARY_ADD,
      );

      if (this.isNewAddBeneficiaryIntlExist) {
        this._router.navigateByUrl('/zones/payments/add-beneficiary-intl#personal');
      } else {
        this._router.navigateByUrl('/zones/payments/intl-beneficiary-bank-details-personal');
      }
    } else {
      this.selectCountryForm.markAllAsTouched();
    }
  }

  public emitLatestValues() {
    if (this.isSubmitTypeAsynchronous) {
      const formObject = this.selectCountryForm.value;
      this.FormChangeEvent.emit({
        currency: formObject.currency?.title,
        countryIso2: formObject.country.key,
        isCountryCurrencyFormValid: this.formGroup.valid,
      });
    }
  }
}
